import { Link } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  Bug,
  Clock,
  Copy,
  Download,
  Flag,
  FlaskConical,
  MoreHorizontal,
  Pause,
  Play,
  Store,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ActorLabel } from '~/components/common/actor-label';
import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { CONNECTOR_LOGOS } from '~/components/logos/connector-logos';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Label } from '~/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';
import { TEMPORAL_NAMESPACE, temporalWorkflowUrl } from '~/constants/config/env';
import {
  BatchDebugPanel,
  type DebugIdEntry,
} from '~/features/catalog-operations/components/batch-detail/batch-debug-panel';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import { useAuthStore } from '~/store/auth.store';
import { copyToClipboard } from '~/utils/clipboard';
import { formatDateTime, formatRelative } from '~/utils/date';

import { isHiddenStage } from '../constants/hidden-stages';
import { useStageMetadata } from '../hooks/use-run';
import { useRunArchive } from '../hooks/use-run-archive';
import { useRunLabels } from '../hooks/use-run-labels';
import { type RunNextAction, useRunNextStep } from '../hooks/use-run-next-step';
import { useRunToggle } from '../hooks/use-run-toggle';
import type { RunProgress, RunRead } from '../types/run.api.types';
import { catalogLabel } from '../utils/catalog-labels';
import { toStorageConnector } from '../utils/lineage-images-format';
import { useCrossLinkSearch } from '../utils/nav-context';
import { formatCount, stageCount } from '../utils/run-format';
import { isRunLive, isRunStopped } from '../utils/run-status';

import { ConnectorMark } from './connector-mark';
import { EntityHeader } from './entity-header';
import { HeaderIdsPopover } from './gate-handoff-ids';
import { HeaderMetaChip } from './header-meta-chip';
import type { Crumb } from './page-breadcrumbs';
import { HelpPanel, HelpTerms } from './page-intro';
import { RunFailureSummary } from './run-failure-summary';
import { RunNowNext } from './run-phase-strip';
import { RunStatusChip } from './run-status-chip';

interface Props {
  runId: string;
  run: RunRead | undefined;
  progress: RunProgress | undefined;
  isLoading: boolean;
  /** The run's name from `useRunTitle` — the same string the breadcrumb shows. */
  title: string;
  experimentId: string | null;
  experimentName: string | null;
  breadcrumbs: Crumb[];
  /** The run tab on screen — the primary action never re-opens it (Q27). */
  activeTab: string;
  /** Rendered under the meta line (the phase strip). */
  children?: ReactNode;
}

/** First 8 chars of a long id — the full value stays one click away (copy). */
function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

/**
 * "How this batch works": the run's RESOLVED template — every stage it will run, in order,
 * ⚑ on the ones that gate for a decision, with a one-line description each. One place answers
 * "what does this flow actually do", instead of the four generic phases alone.
 */
function FlowStagesHelp({
  run,
  destination,
}: {
  run: RunRead | undefined;
  /** Resolved connector name, so DELIVERY reads "Publish to Google Merchant Center" here too. */
  destination: string | null;
}) {
  const { t } = useTranslation('catalog-v2-run');
  const { stageLabel } = useRunLabels();
  // The backend's own per-stage description (B1), preferred over the local copy when it has one
  // for this stage; the stage NAME above always comes from this app's labels file, never here.
  const { data: stageMetadata } = useStageMetadata();
  const remoteDescription = (stage: string): string | null =>
    stageMetadata?.find((m) => m.stage === stage)?.description ?? null;
  const flow = run?.config?.flow;
  if (!flow || flow.stages.length === 0) return null;
  const gated = new Set(flow.gatedStages);
  return (
    <div className="flex flex-col gap-3 border-t pt-5">
      <div>
        <h3 className="text-foreground text-sm font-semibold">{t('header.help.flowTitle')}</h3>
        <p className="text-muted-foreground text-xs">{t('header.help.flowBody')}</p>
      </div>
      <ol className="flex flex-col gap-2.5">
        {/* Image check is hidden from the UI (hidden-stages.ts) — numbering skips it. */}
        {flow.stages
          .filter((stage) => !isHiddenStage(stage))
          .map((stage, i) => (
            <li key={stage} className="flex items-start gap-2 text-sm">
              <span className="text-muted-foreground w-4 shrink-0 text-right text-xs tabular-nums">
                {i + 1}.
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="text-foreground font-medium">
                  {stageLabel(stage, destination)}
                  {gated.has(stage) ? (
                    <Flag
                      aria-label={t('phaseTimeline.decisionPoint')}
                      className="text-warning ml-1 inline size-3 align-[-1px]"
                    />
                  ) : null}
                </span>
                <span className="text-muted-foreground text-xs leading-snug">
                  {remoteDescription(stage) ??
                    t(`header.help.stageDescriptions.${stage}`, {
                      defaultValue: t('header.help.stageDescriptions.generic'),
                    })}
                </span>
              </div>
            </li>
          ))}
      </ol>
    </div>
  );
}

/**
 * The run frame's one-band header (R1): breadcrumb + ⓘ, then the run's name with its
 * state chip and ONE primary action (decide / review / recover / resume / see delivery —
 * what the old next-step banner offered), `⋯` for pause, ids and archive, and one muted
 * meta line (experiment, destination, products, started). Status reads `/progress` first
 * because it polls; the run record fills what progress does not carry.
 */
export function RunHeader({
  runId,
  run,
  progress,
  isLoading,
  title,
  experimentId,
  experimentName,
  breadcrumbs,
  activeTab,
  children,
}: Props) {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCatalog } = useTranslation('catalog-v2');
  const { toggle, isPending, pendingKind } = useRunToggle(runId);
  const { archive, unarchive, isPending: isArchivePending } = useRunArchive(runId);
  const next = useRunNextStep(run, progress, activeTab);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Support/debug entry — a super-admin-only troubleshooting aid, gated exactly like the legacy
  // batch-debug-panel: the `debugger-int` flag AND super admin, so it never reaches customers.
  const { isAllowed: isDebuggerAllowed } = useFeatureGate('debugger-int');
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const showDebug = isDebuggerAllowed && isSuperAdmin;
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  // R3-3 — the experiment chip and the "Decide in experiment" action both leave the batch; carry
  // `from` like every other cross-link out of it.
  const crossLinkSearch = useCrossLinkSearch();

  if (isLoading && !run && !progress) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-7 w-80" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  const runStatus = progress?.runStatus ?? run?.status ?? '';
  // Design round 2 §1A: a stopped run's ONE failure section (title/why/next + the single primary
  // action) replaces the old trio (phase-strip callout, Overview callout, this header's own
  // action button shown separately) — so the "recovery" action renders INSIDE that section only.
  const stopped = isRunStopped(runStatus);
  const paused = progress?.paused ?? false;
  const live = isRunLive(runStatus);
  const connector = progress?.connectorCode ?? run?.connectorCode ?? null;
  const flow = progress?.flow ?? run?.flow ?? run?.config?.flow?.name ?? null;
  const startedAt = progress?.startedAt ?? run?.startedAt ?? null;
  const workflowId = progress?.workflowId ?? run?.workflowRef ?? null;
  const creator = run?.audit.createdBy ?? null;
  const createdByHandle = creator?.handle ?? progress?.createdBy ?? null;
  const trigger = run?.triggerSource ? catalogLabel(tCatalog, 'trigger', run.triggerSource) : null;
  const archived = !!run?.archivedAt;

  // Products: the same figures the phase strip's flow shows (Q9) — extract's size, then the
  // test arm when the run splits one.
  const stages = progress?.stages ?? [];
  const importStage = stages.find((s) => s.stage === 'IMPORT');
  const armStage = stages.find((s) => s.stage === 'ARM_ASSIGNMENT');
  const imported = progress?.funnel?.extracted ?? stageCount(importStage, 'products_succeeded');
  const testArm =
    armStage && armStage.status !== 'PENDING'
      ? stageCount(armStage, 'products_succeeded')
      : undefined;

  // Design round 2 §M-A — a chip per fact instead of a "·"-joined sentence. The destination gets
  // its bundled connector logo when one exists, else a plain Store mark; the person chip reuses
  // the one actor/user-name resolver every "who did this" field in this app goes through.
  // A scheduled run's creator is the worker's service actor ("catalog-worker"): the chip names
  // the schedule that fired it instead.
  const byActor = run?.scheduleName ? (
    <span className="inline-flex">{t('factsRail.bySchedule', { name: run.scheduleName })}</span>
  ) : creator?.actorId || createdByHandle ? (
    <ActorLabel
      actor={{
        id: creator?.actorId ?? null,
        displayName: createdByHandle ?? '',
        kind: creator?.kind ?? 'USER',
        email: null,
        avatarUrl: null,
        resolved: !!createdByHandle,
      }}
      showAvatar
      className="inline-flex"
    />
  ) : null;

  const meta: ReactNode[] = [
    experimentId ? (
      <HeaderMetaChip key="experiment">
        <Link
          to="/catalog-v2/experiments/$experimentId"
          params={{ experimentId }}
          search={crossLinkSearch}
          title={experimentName ?? undefined}
          className="text-primary cursor-pointer hover:underline"
        >
          {t('header.meta.experiment')}
        </Link>
      </HeaderMetaChip>
    ) : null,
    connector ? (
      <HeaderMetaChip
        key="connector"
        icon={
          // C1 — the brand mark via `ConnectorMark`, which normalises the WIRE code (`GMC`) to the
          // logo map's storage key first; the raw lookup here never found Google's logo.
          CONNECTOR_LOGOS[toStorageConnector(connector)] ? (
            <ConnectorMark code={connector} className="[&_svg]:size-3.5" />
          ) : (
            <Store aria-hidden="true" className="size-3.5 shrink-0" />
          )
        }
      >
        <span title={flow ? catalogLabel(tCatalog, 'flow', flow) : undefined}>
          {catalogLabel(tCatalog, 'connector', connector)}
        </span>
      </HeaderMetaChip>
    ) : null,
    imported !== undefined ? (
      <HeaderMetaChip
        key="imported"
        icon={<Download aria-hidden="true" className="size-3.5 shrink-0" />}
      >
        {/* Links to the batch's own product list, like the sibling "experiment" chip links to its
        experiment (round-3 review finding 4) — never a plain, inert fact. */}
        <Link
          to="/catalog-v2/runs/$runId/products"
          params={{ runId }}
          className="text-primary focus-visible:ring-ring cursor-pointer rounded hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          {testArm !== undefined
            ? t('header.meta.productsWithArm', {
                count: imported,
                imported: formatCount(imported),
                testArm: formatCount(testArm),
              })
            : t('header.meta.products', { count: imported, imported: formatCount(imported) })}
        </Link>
      </HeaderMetaChip>
    ) : null,
    startedAt ? (
      <HeaderMetaChip
        key="started"
        icon={<Clock aria-hidden="true" className="size-3.5 shrink-0" />}
      >
        {/* The exact time now sits beside the relative one as text, never hover-only. */}
        {t('header.meta.started', { when: formatRelative(startedAt) })} ·{' '}
        {formatDateTime(startedAt)}
      </HeaderMetaChip>
    ) : (
      <HeaderMetaChip key="notStarted">{t('header.facts.notStarted')}</HeaderMetaChip>
    ),
    startedAt && byActor ? (
      <HeaderMetaChip key="actor">{byActor}</HeaderMetaChip>
    ) : startedAt && trigger ? (
      <HeaderMetaChip key="trigger">{t('header.facts.via', { trigger })}</HeaderMetaChip>
    ) : null,
    // A final chip, always shown: the batch id and this attempt's hand-off ids, moved here out of
    // every gate body's own collapsed DETAILS section (design round 2 §2A/§M-A).
    <HeaderIdsPopover key="ids" runId={runId} progress={progress} activeTab={activeTab} />,
  ];

  /** Copies a run/workflow id and confirms with a toast — kept off the visible row (lives in `⋯`). */
  const copyId = (value: string, message: string) => {
    copyToClipboard(value);
    toast.success(message);
  };

  /** Sends the pause with the optional reason and closes the dialog once accepted. */
  const confirmPause = () => {
    toggle('pause', pauseReason, {
      onSuccess: () => {
        setPauseOpen(false);
        setPauseReason('');
      },
    });
  };

  /** Archives or unarchives the run; the mutation itself toasts the result and closes the dialog once it settles. */
  const confirmArchive = () => {
    const onSettled = () => {
      setArchiveOpen(false);
    };
    if (archived) unarchive({ onSettled });
    else archive({ onSettled });
  };

  /** The ONE primary action for the run's state (replaces the next-step banner). */
  const primary = (action: RunNextAction | null) => {
    if (!action) return null;
    switch (action.kind) {
      case 'experiment':
        return (
          <Button asChild size="sm" className="cursor-pointer">
            <Link
              to="/catalog-v2/experiments/$experimentId"
              params={{ experimentId: action.experimentId }}
              search={crossLinkSearch}
            >
              {t('nextStep.gate.decideInExperiment')}
            </Link>
          </Button>
        );
      case 'review':
        return (
          <Button asChild size="sm" className="cursor-pointer">
            {action.tab === 'overview' ? (
              // The Publish approval is decided in Summary's top band, Approval pane selected.
              <Link to="/catalog-v2/runs/$runId" params={{ runId }} search={{ fStage: 'APPROVAL' }}>
                {t('nextStep.gate.review')}
              </Link>
            ) : (
              <Link to="/catalog-v2/runs/$runId/products" params={{ runId }}>
                {t('nextStep.gate.review')}
              </Link>
            )}
          </Button>
        );
      case 'recovery':
        return (
          <Button asChild size="sm" variant="destructive" className="cursor-pointer">
            <Link to="/catalog-v2/runs/$runId/recovery" params={{ runId }}>
              {t('nextStep.failed.recover')}
            </Link>
          </Button>
        );
      case 'delivery':
        return (
          <Button asChild size="sm" variant="outline" className="cursor-pointer">
            {/* "Did it land" lives in Summary's Publish stage pane (Publishing tab folded in). */}
            <Link to="/catalog-v2/runs/$runId" params={{ runId }} search={{ fStage: 'DELIVERY' }}>
              {t('nextStep.seeDelivery')}
            </Link>
          </Button>
        );
      case 'resume':
        return (
          <Button
            size="sm"
            className="cursor-pointer disabled:cursor-not-allowed"
            onClick={() => {
              toggle('resume');
            }}
            disabled={isPending}
            isLoading={pendingKind === 'resume'}
          >
            <Play aria-hidden="true" />
            {t('header.resume')}
          </Button>
        );
    }
  };
  // Rendered inside the failure status section instead of the header's action row (§1A) — a
  // stopped run's ONE action lives with its ONE explanation, not off to the side of it.
  const recoveryAction = next?.action?.kind === 'recovery' ? primary(next.action) : null;

  const help = (
    <HelpPanel trigger="icon" title={t('header.help.title')} description={t('overview.purpose')}>
      <HelpTerms
        terms={[
          { term: tCatalog('phases.PREPARE'), description: t('header.help.prepare') },
          { term: tCatalog('phases.CHECK_QUALITY'), description: t('header.help.checkQuality') },
          { term: tCatalog('phases.GENERATE'), description: t('header.help.generate') },
          { term: tCatalog('phases.SHIP'), description: t('header.help.ship') },
          { term: t('header.help.decisionTerm'), description: t('header.help.decision') },
          { term: t('header.help.unitsTerm'), description: t('header.help.units') },
        ]}
      />
      <FlowStagesHelp
        run={run}
        destination={connector ? catalogLabel(tCatalog, 'connector', connector) : null}
      />
    </HelpPanel>
  );

  // Raw identifiers for the support/debug panel — field names stay verbatim (untranslated),
  // a troubleshooting view, not customer UI. The Temporal link carries the `agentic-workflow`
  // namespace, where the catalog batch pipeline runs (not the integrations default).
  const workflowRef = progress?.workflowId ?? run?.workflowRef ?? null;
  const debugIds: DebugIdEntry[] = [
    { label: 'runId', value: runId },
    {
      label: 'workflowId',
      value: workflowRef,
      href: temporalWorkflowUrl(workflowRef, TEMPORAL_NAMESPACE.workflow),
    },
    { label: 'catalogId', value: run?.catalogId ?? null },
    { label: 'experimentId', value: experimentId },
    { label: 'scheduleId', value: run?.scheduleId ?? null },
    { label: 'parentRunId', value: run?.parentRunId ?? null },
  ];

  return (
    <>
      <EntityHeader
        breadcrumbs={breadcrumbs}
        help={help}
        title={<span className="break-words">{title}</span>}
        chips={
          <>
            {experimentId ? (
              <span className="bg-info-50 text-info border-info/30 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                <FlaskConical aria-hidden="true" className="size-3" />
                {t('header.experimentChip')}
              </span>
            ) : null}
            {archived ? <RunStatusChip status="ARCHIVED" label={t('header.archived')} /> : null}
          </>
        }
        status={
          next && progress ? (
            // Click-open (never hover-only): the state's hint plus the "Now · Next" sentence the
            // phase strip no longer prints under itself.
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('header.statusDetails', { status: next.label })}
                  title={next.hint ?? undefined}
                  className="h-auto cursor-pointer rounded-full p-0 hover:bg-transparent"
                >
                  <RunStatusChip
                    status={next.status}
                    label={next.label}
                    showIcon
                    className="px-3 py-1 text-sm"
                  />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="flex w-80 flex-col gap-2 p-3">
                {next.hint ? <p className="text-muted-foreground text-sm">{next.hint}</p> : null}
                <RunNowNext runId={runId} progress={progress} />
              </PopoverContent>
            </Popover>
          ) : next ? (
            <span title={next.hint ?? undefined} className="inline-flex">
              <RunStatusChip
                status={next.status}
                label={next.label}
                showIcon
                className="px-3 py-1 text-sm"
              />
            </span>
          ) : null
        }
        actions={
          <>
            {/* The "recovery" action moves into the failure status section below (§1A) — every
            other state's action stays here as before. */}
            {next?.action?.kind !== 'recovery' ? primary(next?.action ?? null) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8 cursor-pointer"
                  aria-label={t('header.moreActions')}
                >
                  <MoreHorizontal aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-w-80">
                {/* Pause lives here: it is never the run's primary action (Resume is, when paused). */}
                {live && !paused ? (
                  <>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      disabled={isPending}
                      onClick={() => {
                        setPauseOpen(true);
                      }}
                    >
                      <Pause aria-hidden="true" />
                      {t('header.pause')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : null}
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => {
                    copyId(runId, t('header.toast.copiedRunId'));
                  }}
                >
                  <Copy aria-hidden="true" />
                  {t('header.copyRunId')} ({shortId(runId)})
                </DropdownMenuItem>
                {workflowId ? (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => {
                      copyId(workflowId, t('header.toast.copiedWorkflowId'));
                    }}
                  >
                    <Copy aria-hidden="true" />
                    {t('header.copyWorkflowId')} ({shortId(workflowId)})
                  </DropdownMenuItem>
                ) : null}
                {run?.parentRunId ? (
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/catalog-v2/runs/$runId" params={{ runId: run.parentRunId }}>
                      {t('header.parentRun')}: {shortId(run.parentRunId)}
                    </Link>
                  </DropdownMenuItem>
                ) : null}
                {run ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="cursor-pointer"
                      disabled={isArchivePending}
                      onClick={() => {
                        setArchiveOpen(true);
                      }}
                    >
                      {archived ? (
                        <ArchiveRestore aria-hidden="true" />
                      ) : (
                        <Archive aria-hidden="true" />
                      )}
                      {archived ? t('header.unarchive') : t('header.archive')}
                    </DropdownMenuItem>
                  </>
                ) : null}
                {showDebug ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => {
                        setIsDebugOpen((open) => !open);
                      }}
                    >
                      <Bug aria-hidden="true" />
                      {t('header.supportDebug')}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        meta={meta}
        metaChips
      >
        {/* The ONE place a stopped batch's failure shows (§1A) — title, why, next step, and the
        single "Recover" action, all in one section instead of three scattered callouts. */}
        {progress && stopped ? (
          <RunFailureSummary runId={runId} progress={progress} action={recoveryAction} />
        ) : null}
        {children}
      </EntityHeader>

      <ConfirmDialog
        open={pauseOpen}
        onOpenChange={(open) => {
          if (!isPending) setPauseOpen(open);
        }}
        title={t('header.pauseDialog.title')}
        description={
          <div className="flex flex-col gap-3 text-left">
            <p>{t('header.pauseDialog.body')}</p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="run-pause-reason">{t('header.pauseDialog.reason')}</Label>
              <Textarea
                id="run-pause-reason"
                value={pauseReason}
                onChange={(e) => {
                  setPauseReason(e.target.value);
                }}
                rows={2}
              />
            </div>
          </div>
        }
        confirmLabel={t('header.pauseDialog.confirm')}
        cancelLabel={t('common.cancel')}
        isPending={pendingKind === 'pause'}
        onConfirm={confirmPause}
      />

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={(open) => {
          if (!isArchivePending) setArchiveOpen(open);
        }}
        title={
          archived ? t('header.archiveDialog.unarchiveTitle') : t('header.archiveDialog.title')
        }
        description={
          archived ? t('header.archiveDialog.unarchiveBody') : t('header.archiveDialog.body')
        }
        confirmLabel={archived ? t('header.unarchive') : t('header.archive')}
        cancelLabel={t('common.cancel')}
        isPending={isArchivePending}
        onConfirm={confirmArchive}
      />

      {/* Floating, non-modal debug panel — gated exactly like legacy batch-debug-panel. */}
      {showDebug ? (
        <BatchDebugPanel
          open={isDebugOpen}
          ids={debugIds}
          title={t('header.debugPanel.title')}
          subtitle={t('header.debugPanel.subtitle')}
          onClose={() => {
            setIsDebugOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
